import type { FlowUserInfo } from '@eci-saas/shared'

export const useUserStore = defineStore(
  'contents_user',
  () => {
    const userInfo = ref<FlowUserInfo | null>(null)
    const accessToken = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const supabaseToken = ref<string | null>(null)
    const initialized = ref(false)
    const loading = ref(false)
    const userUUID = ref<string>('')

    // 重置状态
    const reset = () => {
      userInfo.value = null
      accessToken.value = null
      refreshToken.value = null
      supabaseToken.value = null
      userUUID.value = ''
    }

    // 设置用户信息
    const setUserInfo = (newUserInfo: FlowUserInfo | null) => {
      userInfo.value = newUserInfo
      // 将数字用户ID转换为UUID格式字符串（补0）
      if (newUserInfo?.userId) {
        const paddedId = String(newUserInfo.userId).padStart(32, '0')
        // 格式化为标准UUID格式: 8-4-4-4-12
        userUUID.value = [
          paddedId.slice(0, 8),
          paddedId.slice(8, 12),
          paddedId.slice(12, 16),
          paddedId.slice(16, 20),
          paddedId.slice(20, 32),
        ].join('-')
      }
      else {
        userUUID.value = ''
      }
    }

    // 设置认证tokens
    const setTokens = (access: string, refresh: string) => {
      accessToken.value = access
      refreshToken.value = refresh
    }

    // 设置Supabase查询token
    const setSupabaseToken = (token: string) => {
      supabaseToken.value = token
    }

    // 初始化用户状态
    const initializeAuth = async () => {
      if (initialized.value)
        return

      loading.value = true

      try {
        // 尝试从 localStorage 中恢复认证状态

        const storedData = localStorage.getItem('contents_user')
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData)

            // 恢复用户信息
            if (parsedData.userInfo) {
              setUserInfo(parsedData.userInfo)
            }

            // 恢复 tokens
            if (parsedData.accessToken && parsedData.refreshToken) {
              setTokens(parsedData.accessToken, parsedData.refreshToken)
            }

            // 恢复 supabase token
            if (parsedData.supabaseToken) {
              setSupabaseToken(parsedData.supabaseToken)
            }
          }
          catch (parseError) {
            console.error('Failed to parse stored auth data:', parseError)
            // 清除损坏的数据
            localStorage.removeItem('contents_user')
          }
        }

        // 检查恢复后的状态
        if (accessToken.value && userInfo.value) {
          // 如果有存储的用户信息，认为已登录
        }
        else {
          // 没有有效的认证信息
        }
      }
      catch (error) {
        console.error('Failed to initialize auth:', error)
        reset()
      }
      finally {
        loading.value = false
        initialized.value = true
      }
    }

    // 检查用户是否已登录
    const isAuthenticated = computed(() => {
      return !!userInfo.value && !!accessToken.value && !loading.value
    })

    // 获取用户显示名称
    const displayName = computed(() => {
      if (!userInfo.value)
        return ''

      // 优先使用昵称，其次使用姓名
      if (userInfo.value.nickName)
        return userInfo.value.nickName
      if (userInfo.value.name)
        return userInfo.value.name

      // 备用：使用邮箱
      return userInfo.value.email || 'User'
    })

    // 获取用户头像
    const avatarUrl = computed(() => {
      if (!userInfo.value)
        return ''
      return userInfo.value.icon || ''
    })

    return {
      // 只读状态
      userInfo: readonly(userInfo),
      userUUID: readonly(userUUID),
      accessToken: readonly(accessToken),
      refreshToken: readonly(refreshToken),
      supabaseToken: readonly(supabaseToken),
      initialized: readonly(initialized),
      loading: readonly(loading),

      // 计算属性
      isAuthenticated,
      displayName,
      avatarUrl,

      // 状态管理方法
      setUserInfo,
      setTokens,
      setSupabaseToken,
      initializeAuth,
      reset,
    }
  },
  {
    persist: true,
  },
)
